/**
 * ComposerActualite — carte de publication en tête du fil d'actualité.
 *
 * L'étudiant peut publier un souvenir / événement / info avec photo ou courte
 * vidéo. Image compressée côté client (browser-image-compression) avant envoi.
 * La vidéo n'est pas compressée (trop lourd en pur JS) — limite serveur 25 Mo.
 */
import React, { useRef, useState } from 'react';
import { Image as ImageIcon, Video, Send, X, Loader2, Camera } from 'lucide-react';
import { compressImage, formatBytes } from '../../utils/imageCompression';
import { portailApi } from '../services/portailApi';

interface Props {
  auteurNomAffiche: string;
  onPublie: () => void;
}

const TYPES = [
  { code: 'info', label: 'Information' },
  { code: 'evenement', label: 'Événement' },
  { code: 'souvenir', label: 'Souvenir' },
  { code: 'autre', label: 'Autre' },
];

export const ComposerActualite: React.FC<Props> = ({ auteurNomAffiche, onPublie }) => {
  const [ouvert, setOuvert] = useState(false);
  const [titre, setTitre] = useState('');
  const [contenu, setContenu] = useState('');
  const [type, setType] = useState('info');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageInfo, setImageInfo] = useState<string | null>(null);
  const [video, setVideo] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [compressing, setCompressing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const imgRef = useRef<HTMLInputElement>(null);
  const vidRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setTitre(''); setContenu(''); setType('info');
    setImage(null); setImagePreview(null); setImageInfo(null);
    setVideo(null); setVideoPreview(null);
    setError(null); setOuvert(false);
  };

  const onImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    setCompressing(true);
    try {
      const { file: out, originalSize, compressedSize } = await compressImage(f, {
        maxSizeMB: 1, maxWidthOrHeight: 1600, initialQuality: 0.82,
      });
      setImage(out);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target?.result as string);
      reader.readAsDataURL(out);
      setImageInfo(compressedSize < originalSize
        ? `${formatBytes(originalSize)} → ${formatBytes(compressedSize)}`
        : formatBytes(compressedSize));
      setVideo(null); setVideoPreview(null);
    } catch (err: any) {
      setError(err?.message || 'Échec traitement image');
    } finally {
      setCompressing(false);
      if (imgRef.current) imgRef.current.value = '';
    }
  };

  const onVideo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    if (f.size > 25 * 1024 * 1024) {
      setError('Vidéo trop volumineuse (max 25 Mo).');
      return;
    }
    setVideo(f);
    setVideoPreview(URL.createObjectURL(f));
    setImage(null); setImagePreview(null); setImageInfo(null);
    if (vidRef.current) vidRef.current.value = '';
  };

  const publier = async () => {
    setError(null);
    if (!titre.trim()) { setError('Un titre est requis'); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('titre', titre.trim());
      fd.append('contenu', contenu.trim());
      fd.append('type', type);
      if (image) fd.append('image', image);
      if (video) fd.append('video', video);
      await portailApi.actualitePublier(fd);
      reset();
      onPublie();
    } catch (e: any) {
      setError(e?.message || 'Publication échouée');
    } finally {
      setSending(false);
    }
  };

  if (!ouvert) {
    return (
      <button
        type="button"
        onClick={() => setOuvert(true)}
        className="w-full bg-gray-900 border border-gray-800 hover:border-amber-600 rounded-xl p-3.5 flex items-center gap-3 transition-colors"
      >
        <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-500/60 flex items-center justify-center text-amber-300 text-xs font-bold">
          {(auteurNomAffiche || '?').slice(0, 1).toUpperCase()}
        </div>
        <span className="flex-1 text-left text-sm text-gray-400">
          Partagez un souvenir, un événement, une info…
        </span>
        <ImageIcon size={16} className="text-gray-500" />
        <Video size={16} className="text-gray-500" />
      </button>
    );
  }

  return (
    <div className="bg-gray-900 border border-amber-700/40 rounded-xl overflow-hidden">
      <div className="px-4 py-2.5 border-b border-gray-800 flex items-center justify-between">
        <span className="text-xs font-semibold text-amber-300 uppercase tracking-wider">
          Nouvelle publication
        </span>
        <button type="button" onClick={reset} className="text-gray-400 hover:text-gray-200">
          <X size={16} />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <input
          type="text"
          value={titre}
          onChange={(e) => setTitre(e.target.value)}
          maxLength={200}
          placeholder="Titre de la publication"
          className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded text-sm text-gray-100 placeholder-gray-500 focus:border-amber-600 focus:outline-none"
        />
        <textarea
          value={contenu}
          onChange={(e) => setContenu(e.target.value)}
          maxLength={5000}
          rows={4}
          placeholder="Racontez quelque chose… (facultatif)"
          className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded text-sm text-gray-200 placeholder-gray-500 focus:border-amber-600 focus:outline-none resize-none"
        />
        <div className="flex gap-1.5 flex-wrap">
          {TYPES.map(t => (
            <button
              key={t.code}
              type="button"
              onClick={() => setType(t.code)}
              className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                type === t.code
                  ? 'bg-amber-500/20 border-amber-500 text-amber-200'
                  : 'bg-gray-950 border-gray-800 text-gray-400 hover:border-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {(imagePreview || videoPreview) && (
          <div className="relative rounded-lg overflow-hidden border border-gray-800 bg-black">
            {imagePreview && <img src={imagePreview} alt="" className="w-full max-h-64 object-contain" />}
            {videoPreview && (
              <video src={videoPreview} controls className="w-full max-h-64" />
            )}
            <button
              type="button"
              onClick={() => {
                setImage(null); setImagePreview(null); setImageInfo(null);
                setVideo(null); setVideoPreview(null);
              }}
              className="absolute top-2 right-2 p-1.5 bg-black/70 text-white rounded-full hover:bg-black"
              aria-label="Retirer"
            >
              <X size={14} />
            </button>
            {imageInfo && (
              <span className="absolute bottom-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-black/70 text-emerald-300 font-medium">
                {imageInfo}
              </span>
            )}
          </div>
        )}

        {error && (
          <div className="p-2 text-xs bg-red-900/30 border border-red-800 text-red-200 rounded">
            {error}
          </div>
        )}

        <input ref={imgRef} type="file" accept="image/*" onChange={onImage} className="hidden" />
        <input ref={vidRef} type="file" accept="video/*" onChange={onVideo} className="hidden" />

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => imgRef.current?.click()}
            disabled={compressing || sending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-950 border border-gray-800 hover:border-gray-700 text-xs text-gray-300 rounded transition-colors disabled:opacity-50"
          >
            {compressing ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
            {compressing ? 'Compression…' : 'Photo'}
          </button>
          <button
            type="button"
            onClick={() => vidRef.current?.click()}
            disabled={sending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-950 border border-gray-800 hover:border-gray-700 text-xs text-gray-300 rounded transition-colors disabled:opacity-50"
          >
            <Video size={13} /> Vidéo
          </button>
          <button
            type="button"
            onClick={publier}
            disabled={!titre.trim() || sending || compressing}
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:hover:bg-amber-600 text-black text-xs font-semibold rounded transition-colors"
          >
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Publier
          </button>
        </div>
      </div>
    </div>
  );
};
